import { useMutation } from '@apollo/client';
import * as _ from 'lodash-es';
import type { Op } from 'quill/core';
import React, { useState } from 'react';
import * as styles from './EditingOrganizationSummary.module.scss';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Button, { ButtonWithLoadingState } from '../../components/Button';
import RichTextDescriptionEditor from '../../editor/RichTextDescriptionEditor';
import { gql } from '../../graphql/generated/gql';
import { UIActions } from '../../ui/uiSlice';
import { useBeforeLeaveConfirm } from '../../utils/BeforeLeaveConfirm';

const ORGANIZATION_SUMMARY_MUTATION = gql(/** @complexity 2 */ `
  mutation updateOrganizationSummary($summary: Json) {
    organization: updateOrganization(summary: $summary) {
      id
      summary
    }
  }
`);

interface EditingOrganizationSummaryProps {
  summary: Op[];
  onCancelEditing: () => void;
}

const EditingOrganizationSummary = ({
  summary,
  onCancelEditing,
}: EditingOrganizationSummaryProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [latestContent, setLatestContent] = useState(summary);

  const [updateSummary] = useMutation(ORGANIZATION_SUMMARY_MUTATION);
  const dispatch = useAppDispatch();

  const isChanged = !_.isEqual(summary, latestContent);

  useBeforeLeaveConfirm({ skip: !isChanged });

  return (
    <div className={styles.container}>
      <RichTextDescriptionEditor
        autoFocus
        namespace="organizationSummary"
        defaultValue={summary}
        readOnly={false}
        contentPlaceholder="Welcome your teammates, give directions, or make a joke…"
        onTextChange={setLatestContent}
      />
      <div className={styles.buttons}>
        <ButtonWithLoadingState
          variant="primary"
          disabled={!isChanged}
          loading={isSaving}
          onClick={() => {
            setIsSaving(true);
            updateSummary({
              variables: { summary: JSON.stringify(latestContent) },
            })
              .then(() => {
                setIsSaving(false);
                onCancelEditing();
              })
              .catch(() => {
                setIsSaving(false);
                dispatch(
                  UIActions.pushToast({
                    level: 'error',
                    title: 'Unable to save changes',
                    message:
                      'An error occurred while saving. Please try again later.',
                  }),
                );
              });
          }}
        >
          Save
        </ButtonWithLoadingState>
        <Button
          variant="secondary"
          disabled={isSaving}
          onClick={onCancelEditing}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default EditingOrganizationSummary;
